"use client";

import { useEffect, useRef } from "react";
import { useResource } from "@/lib/use-resource";
import { toast } from "@/components/toast-provider";

type Live = { state: string; reachable: boolean };
type Overview = { id: string; live?: Live };
type ServerRef = { id: string; shortId?: string; name: string };

type Snapshot = { state: string; reachable: boolean };

// Watches server state in the background and raises a toast on meaningful changes.
export function ServerWatcher() {
  const overview = useResource<{ servers: Overview[] }>("/servers/overview", 10000);
  const servers = useResource<{ servers: ServerRef[] }>("/servers", 60000);
  const previous = useRef<Map<string, Snapshot> | null>(null);

  useEffect(() => {
    const list = overview.data?.servers;
    if (!list) return;
    const names = new Map((servers.data?.servers || []).map((s) => [s.id, s]));
    const next = new Map<string, Snapshot>();
    for (const item of list) {
      if (!item.live) continue;
      next.set(item.id, { state: item.live.state, reachable: item.live.reachable });
    }
    const before = previous.current;
    previous.current = next;
    // The first poll only establishes a baseline so opening the panel is quiet.
    if (!before) return;
    for (const [id, now] of next) {
      const was = before.get(id);
      const ref = names.get(id);
      if (!was || !ref) continue;
      const href = `/servers/${ref.shortId || id}`;
      const key = `server:${id}`;
      if (was.reachable && !now.reachable) {
        toast({ key, tone: "error", title: `${ref.name} is unreachable`, message: "The node is not responding.", href });
      } else if (!was.reachable && now.reachable) {
        toast({ key, tone: "success", title: `${ref.name} is reachable again`, href });
      } else if (now.reachable && was.state !== now.state) {
        if (now.state === "running") toast({ key, tone: "success", title: `${ref.name} started`, href });
        else if (was.state === "running" && (now.state === "dead" || now.state === "restarting"))
          toast({ key, tone: "error", title: `${ref.name} crashed`, message: "Open the crash doctor to see why.", href, actionLabel: "Investigate" });
        else if (was.state === "running" && (now.state === "exited" || now.state === "created"))
          toast({ key, tone: "info", title: `${ref.name} stopped`, href });
      }
    }
  }, [overview.data, servers.data]);

  return null;
}
